import * as z from "zod";
import { isUuid } from "../index";

// The rooms of Nexxt Live (ADR-276): one Durable Object per room. A room is named after what changes in it, never
// after what it shows, so the domain that writes and the screen that reads find it without asking each other.

/** `<schema>.<table>:<id>`: the aggregate whose history shows the change (ADR-052), e.g. a purchase order. */
const ENTITY_ROOM = /^[a-z][a-z_]*\.[a-z][a-z_]*:[A-Za-z0-9_-]{1,64}$/;
const USER_PREFIX = "user:";

/**
 * A user as the rooms know them: a uuid in lowercase, so one user is one user room and one socket tag whatever the
 * caller wrote.
 */
export const liveUserId = z
  .string()
  .refine(isUuid, "Not a uuid")
  .transform((id) => id.toLowerCase());

/** The room of an aggregate, as `audit()` names it: `entityRoom("procurement.purchase_order", id)`. */
export const entityRoom = (type: string, id: string): string => `${type}:${id}`;

/**
 * The aggregates whose reads give a grant (ADR-277): `<schema>.<table>`, the type their audit entries name, so the
 * gateway grants the room the domain signals.
 */
export const liveAggregates = {
  purchaseOrder: "procurement.purchase_order",
  inbound: "procurement.inbound",
  linesheet: "catalog.linesheet",
  attributeTask: "catalog.attribute_task",
  importBatch: "platform.import_batch",
  salesOrder: "sales.sales_order",
  orderConfirmationRun: "sales.order_confirmation_run",
  allocationRun: "sales.allocation_run",
  fulfillment: "logistics.fulfillment",
  /** A vendor return too: it is a stock movement, with the same id. */
  stockMovement: "logistics.stock_movement",
  organization: "party.organization",
  claim: "claims.claim",
  /** Its availabilities, links and invitations (ADR-291). */
  salesCampaign: "booking.sales_campaign",
  /** Its documents, block by block (ADR-293): a person and Claude edit it together. */
  emailTemplate: "crm.email_template",
  /** The invitations of a campaign as the projection of crm has them (ADR-299): the matrix of Guestlist. */
  crmCampaign: "crm.campaign_profile",
  /** An account of Guestlist: its warmth moves with a human signal of its people (ADR-316 §1). */
  crmAccount: "crm.account_profile",
  /** A send of Guestlist (ADR-319, ADR-316): its status, waves and counts, signalled by its steps and its controls. */
  crmSend: "crm.send",
} as const;

/**
 * The aggregates whose screens show who else is there (ADR-279): only for their rooms the gateway reads the profile
 * of the person, and a member without one is neither shown nor shown anyone.
 */
const presenceAggregates: ReadonlySet<string> = new Set([
  liveAggregates.purchaseOrder,
  liveAggregates.inbound,
  liveAggregates.linesheet,
  liveAggregates.attributeTask,
  // who is on which block: `row` is its id (ADR-293)
  liveAggregates.emailTemplate,
  // who watches a send go out: its live control room (ADR-316 §6)
  liveAggregates.crmSend,
]);

/** Whether the screens of `room` show who else is there. */
export const showsPresence = (room: string): boolean => presenceAggregates.has(room.slice(0, room.indexOf(":")));

/**
 * The rules of the simulator of TIL (ADR-266), all in one room: the page lists them, and a message sent in the
 * background uses one (ADR-285).
 */
export const tilMockRulesRoom = entityRoom("platform.til_mock_rule", "all");

/**
 * The counts of the home of the internal users (ADR-285): every live operation that changed something signals it with
 * its rooms (`executeAndPublish`), because no domain knows which of its writes moves a queue of the worklist. The
 * home of Backstage reads it again at most every few seconds; "Today" of Guestlist has a room of its own
 * (`crmTodayRoom`).
 */
export const internalQueuesRoom = entityRoom("worklist.queues", "internal");

/**
 * "Today" of Guestlist (ADR-299 §7.4, ADR-300 §7.3): every write of the projection of crm signals it, since all that
 * Today shows comes from there, and `GET /crm/today` of gateway-guestlist grants it. Not the counts of the home: the
 * read costs up to a hundred milliseconds on the volumes of a few seasons, and the writes of the whole suite would ask
 * it of every open tab (Q-SEC-35, point 2).
 */
export const crmTodayRoom = entityRoom("crm.today", "all");

/**
 * "Hot right now" of Guestlist (ADR-316 §2): crm signals it after a human signal of the email or a step of a booking
 * link, and `GET /crm/hot` of gateway-guestlist grants it.
 */
export const crmHotRoom = entityRoom("crm.hot", "all");

/** The room of one user: what is for them alone (the bell, the closing of a revoked user), and where their rooms are. */
export const userRoom = (userId: string): string => `${USER_PREFIX}${userId.toLowerCase()}`;

export const isUserRoom = (room: string): boolean => {
  const id = room.slice(USER_PREFIX.length);
  return room.startsWith(USER_PREFIX) && isUuid(id) && id === id.toLowerCase();
};

/** A name a room can have: an aggregate whose id is not a plain key (none today) has no room, and no signal. */
export const isLiveRoom = (room: string): boolean => ENTITY_ROOM.test(room) || isUserRoom(room);

export const liveRoomName = z.string().refine(isLiveRoom, "Not the name of a live room");

/**
 * Where the object of a room is born, for every worker that reaches one (`getByName(room, liveRoomLocation)`): only
 * the first call creates it, so all of them ask the same. Western Europe, next to the gateways and the domains
 * (aws:eu-central-1); the spike measured AMS, 16–55 ms from the gateway (P8.12a).
 */
export const liveRoomLocation = { locationHint: "weur" } as const;
